import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { RiDashboardFill } from "react-icons/ri";
import { FaUserAlt, FaCog, FaChartLine, FaTools, FaUsers, FaShoppingCart, FaDatabase, FaRegMoneyBillAlt, FaBars, FaTimes, FaUserCog, FaPeopleArrows, FaBoxOpen, FaRobot, FaKey } from "react-icons/fa";
import { FcSalesPerformance } from "react-icons/fc";
import { VscOpenPreview } from "react-icons/vsc";
import { FaShop } from "react-icons/fa6";
import { GoGraph } from "react-icons/go";

import LogoOpen from "../../Images/logo.png";
import LogoClose from "../../Images/logo.png";
import { useTheme } from "../BrandColors/Context/ThemeContext";

function SideBar({ isOpen, setIsOpen, sidebarRef }) {
  const dealershipTheme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));

  const theme = user?.role === "DealershipAdmin" ? dealershipTheme : "#3b82f6";

  const [activeItem, setActiveItem] = useState(location.pathname);

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  const filteredModules = user?.assignedModules?.filter((module) => module.permissions.includes("read"));
  const filteredProducts = user?.assignedProducts?.filter((product) => product.permissions.includes("read"));

  const rawCombinedItems = [
    ...(filteredModules || [])
      .filter((module) => module?.module?.name === "Dashboard")
      .map((module) => ({
        id: module?.module?._id,
        name: module?.module?.name.trim(),
        type: "module",
      })),
    ...(filteredModules || [])
      .filter((module) => module?.module?.name && module?.module?.name !== "Dashboard")
      .map((module) => ({
        id: module?.module?._id,
        name: module?.module?.name.trim(),
        type: "module",
      })),
    ...(filteredProducts || [])
      .filter((product) => product?.product?.name)
      .map((product) => ({
        id: product?.product?._id,
        name: product?.product?.name.trim(),
        type: "product",
      })),
  ].filter((item) => {
    if (user?.role === "Owner" && ["Deals", "Customer", "Cars"].includes(item.name)) {
      return false;
    }
    return true;
  });

  const combinedItems = user?.role === "Owner"
    ? [
        ...rawCombinedItems,
        { id: "chatbot-overview-sidebar-id", name: "Overview Chatbot", type: "module" },
        { id: "agent-lab-sidebar-id", name: "Agent Lab", type: "module" },
        { id: "demo-requests-sidebar-id", name: "Demo Requests", type: "module" },
        { id: "api-keys-sidebar-id", name: "API Keys", type: "module" }
      ]
    : [
        ...rawCombinedItems,
        { id: "demo-requests-sidebar-id", name: "Demo Requests", type: "module" },
        { id: "campaigns-sidebar-id", name: "Campaigns", type: "module" }
      ];

  const itemIcons = {
    "Demo Requests": FaUsers,
    Dashboard: RiDashboardFill,
    "User Management": FaUsers,
    "Avatar Selection": FaTools,
    Products: FaShoppingCart,
    Subscriptions: FaDatabase,
    "Admin Dashboard": FaUserCog,
    Dealership: FaCog,
    Deals: FaTools,
    Customer: FaPeopleArrows,
    "F&I Service": FaRegMoneyBillAlt,
    "Pre-Sales Engagement": FcSalesPerformance,
    "Review Service": VscOpenPreview,
    "Enrollment and Finance": FaBoxOpen,
    Marketing: FaShop,
    TeleAssist: GoGraph,
    "Pitch Dealership": GoGraph,
    "Overview Chatbot": FaChartLine,
    "Agent Lab": FaRobot,
    "API Keys": FaKey,
    Campaigns: FaRobot,
  };

  const getItemIcon = (itemName) => itemIcons[itemName] || FaChartLine;

  const toggleSidebar = () => setIsOpen(!isOpen);

  return (
    <div
      ref={sidebarRef}
      className={`fixed top-0 left-0 h-screen bg-blue-50 border-r border-blue-200/50 shadow-2xl transition-all duration-300 z-50 flex flex-col ${isOpen ? "w-52" : "w-16"
        }`}
    >
      <div
        className="absolute -right-3 top-6 bg-white border border-blue-200 text-slate-700 p-1.5 rounded-full cursor-pointer hover:text-white hover:bg-slate-700 z-50 transition-colors shadow-md"
        onClick={toggleSidebar}
      >
        {isOpen ? <FaTimes size={10} /> : <FaBars size={10} />}
      </div>

      <div className="flex items-center justify-center h-16 border-b border-blue-300/50">
        <img
          src={isOpen ? LogoOpen : LogoClose}
          alt="Sale Sense Logo"
          className={`transition-all duration-300 ${isOpen ? "h-8" : "h-6"}`}
        />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide py-4 px-2">
        <ul className="flex flex-col gap-1">
          {combinedItems.map((item) => {
            const path = user?.role === "Owner" && item?.name?.toLowerCase() === "dashboard"
              ? "/"
              : `/${item?.name?.toLowerCase()?.replace(/\s+/g, "-")}`;

            const isActive = activeItem === path;

            return (
              <li
                key={item.id}
                onClick={() => {
                  setActiveItem(path);
                  navigate(path);
                  if (window.innerWidth < 768) setIsOpen(false);
                }}
                className={`relative flex items-center p-2 rounded-lg cursor-pointer transition-all duration-300 group ${isActive ? "bg-slate-800/80 text-white font-semibold shadow-md" : "text-slate-600 hover:bg-white hover:text-slate-800"
                  }`}
                style={isActive ? { borderLeft: `3px solid ${theme}` } : { borderLeft: '3px solid transparent' }}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-transparent opacity-10 rounded-lg" style={{ backgroundImage: `linear-gradient(to right, ${theme}, transparent)` }}></div>
                )}

                <span className={`text-lg z-10 transition-transform duration-300 ${isOpen ? "mr-3" : "mx-auto"} ${isActive ? "" : "group-hover:scale-110"}`} style={isActive ? { color: theme } : {}}>
                  {React.createElement(getItemIcon(item.name))}
                </span>

                {isOpen && (
                  <span className="z-10 text-xs whitespace-nowrap overflow-hidden text-ellipsis">{item.name}</span>
                )}

                {!isOpen && (
                  <div className="absolute left-full ml-2 px-3 py-1 bg-white text-slate-800 text-xs font-medium rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 border border-blue-200 shadow-xl">
                    {item.name}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default SideBar;
